import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { InventoryResult, InventorySnapshot } from "@ranfac/shared";
import { downloadInventoryJe, fetchInventory } from "../api.js";
import { WorkpaperActions } from "../components/WorkpaperActions.js";

export function Inventory() {
  const { period = "2026-01" } = useParams();
  const periodEnd = periodEndOf(period);
  const [data, setData] = useState<{
    snapshot: InventorySnapshot;
    result: InventoryResult;
    source: "upload" | "fixture";
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchInventory(periodEnd)
      .then(setData)
      .catch((e) => setErr(String(e)));
  }, [periodEnd]);

  if (err) return <div className="banner warn">Could not load: {err}</div>;
  if (!data) return <div className="card">Loading…</div>;

  const { result, source } = data;
  const tiedOut = Math.abs(result.varianceVsValuation) < 0.01;

  async function onDownloadJe() {
    setBusy(true);
    try {
      const { filename, csv } = await downloadInventoryJe(periodEnd);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setToast(`Downloaded ${filename}. Import via Library Master → GL Import.`);
    } catch (e) {
      setToast(`JE generation failed: ${e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="card">
        <div style={{ marginBottom: 8 }}>
          <Link to={`/close/${period}`}>&larr; back to close dashboard</Link>
        </div>
        <h1>Inventory Reconciliation</h1>
        <div className="subtle">
          GL 1105 – 1198 (F/G, Raw Material, WIP) and 1198-100 / 5710 / 5740
          (Prepaid Supplies) · period ending {result.periodEnd} ·{" "}
          {result.lines.length} GL accounts ·{" "}
          {source === "upload" ? (
            <span
              className="badge"
              style={{ background: "#dcfce7", color: "#166534" }}
            >
              uploaded file
            </span>
          ) : (
            <span className="badge warn">
              sample data — upload Trial Balance + Inventory Valuation to use
              live numbers
            </span>
          )}
        </div>

        <div className="meta" style={{ marginTop: 16 }}>
          <div className="item">
            <div className="label">Sage source</div>
            <div>
              GL_AccountHistory (preliminary balances) + IM_ItemCost /
              IM_ItemWarehouse (valuation)
            </div>
          </div>
          <div className="item">
            <div className="label">Calculation</div>
            <div>
              Per-GL variance: valuation − (preliminary + adjustments). JE
              posts to {result.adjustmentGl}.
            </div>
          </div>
          <div className="item">
            <div className="label">Phase</div>
            <div>2 · MVP</div>
          </div>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi label="Preliminary GL" value={fmt(result.totalPreliminary)} />
        <Kpi
          label="Adjustments"
          value={fmt(result.totalAdjustments)}
          variant={result.totalAdjustments !== 0 ? "warn" : undefined}
        />
        <Kpi label="Adjusted GL" value={fmt(result.totalFinal)} />
        <Kpi
          label="Valuation Report"
          value={fmt(result.totalValuation)}
          variant={tiedOut ? "ok" : "warn"}
        />
      </div>

      <div className="card">
        <h2>Section totals</h2>
        <table>
          <thead>
            <tr>
              <th>Section</th>
              <th className="num">Preliminary GL</th>
              <th className="num">Adjustments</th>
              <th className="num">Adjusted GL</th>
              <th className="num">Valuation</th>
            </tr>
          </thead>
          <tbody>
            {result.sections.map((s) => (
              <tr key={s.section}>
                <td>{s.label}</td>
                <td className="num">{fmt(s.preliminary)}</td>
                <td className="num">
                  {s.adjustments === 0 ? "—" : fmt(s.adjustments)}
                </td>
                <td className="num">{fmt(s.final)}</td>
                <td className="num">{fmt(s.valuation)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {result.linesWithAdjustments > 0 && (
        <div className="banner warn">
          {result.linesWithAdjustments} GL account
          {result.linesWithAdjustments === 1 ? " has" : "s have"} a preparer
          adjustment to bring the book into agreement with the Valuation
          Report. Reviewer should confirm before sign-off.
        </div>
      )}

      <div className="toolbar">
        <button onClick={onDownloadJe} disabled={busy || result.totalAdjustments === 0}>
          Generate JE (CSV)
        </button>
      </div>

      <WorkpaperActions reconId="inventory" period={period} />

      <div className="card">
        <h2>Per-GL detail</h2>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>GL</th>
                <th>Description</th>
                <th>Section</th>
                <th className="num">Preliminary</th>
                <th className="num">Adj</th>
                <th className="num">Adjusted GL</th>
                <th className="num">Valuation</th>
                <th className="num">Δ (val − adj'd)</th>
              </tr>
            </thead>
            <tbody>
              {result.lines.map((line, i) => (
                <tr
                  key={`${line.row.glAccount}-${i}`}
                  className={line.hasAdjustment ? "row-has-adj" : ""}
                >
                  <td className="monoish">{line.row.glAccount}</td>
                  <td>{line.row.description}</td>
                  <td className="subtle">{line.row.section}</td>
                  <td className="num">{fmt(line.row.preliminaryGlBalance)}</td>
                  <td className="num">
                    {line.hasAdjustment ? fmt(line.row.adjustmentNeeded) : "—"}
                  </td>
                  <td className="num">{fmt(line.row.finalGlBalance)}</td>
                  <td className="num">{fmt(line.row.valuationReportAmount)}</td>
                  <td className="num">
                    {Math.abs(line.varianceVsValuation) < 0.01
                      ? "—"
                      : fmt(line.varianceVsValuation)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div className="toast" onClick={() => setToast(null)}>
          {toast}
        </div>
      )}
    </>
  );
}

function Kpi({
  label,
  value,
  variant,
}: {
  label: string;
  value: string | number;
  variant?: "ok" | "warn";
}) {
  return (
    <div className={`kpi ${variant ? "variance" : ""}`}>
      <div className="label">{label}</div>
      <div className={`value ${variant ?? ""}`}>{value}</div>
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function periodEndOf(period: string): string {
  const [y, m] = period.split("-");
  const year = Number(y);
  const month = Number(m);
  const last = new Date(Date.UTC(year, month, 0));
  return last.toISOString().slice(0, 10);
}
